import type {
  AdminVehicleCatalogModelDto,
  AdminVehicleCatalogPatchModelBody,
} from '@carcheck/shared';
import { zodResolver } from '@hookform/resolvers/zod';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Typography from '@mui/material/Typography';
import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import { AliasSection } from './AliasSection';
import { EntityFields } from './EntityFields';
import { GenerationsSection } from './GenerationsSection';
import { StaleBanner } from './StaleBanner';
import { patchCatalogModel, useCatalogCache } from '../../../api/admin-vehicle-catalog';
import {
  modelFormSchema,
  modelPatchBody,
  modelValues,
  type ModelFormValues,
} from '../_lib/catalogForms';
import type { UseCatalogWriteResult } from '../_lib/useCatalogWrite';
import { useEntityForm } from '../_lib/useEntityForm';

/**
 * One model's edit dialog (AUT-437) — one dialog for the ~280 rows, never a
 * form per row. `model` is read from the make's cached detail on every
 * render, so after an alias write (refetched by the runner) the next PATCH
 * carries the moved `updatedAt`. PATCH answers a `ModelDto` → spliced.
 *
 * AUT-450 — «Поколения» saves on its own (POST / PATCH per generation);
 * «Сохранить» covers only the name and the flags (the footer says so).
 * A generation write does not move the model's `updatedAt`.
 */
export interface ModelEditDialogProps {
  makeId: string;
  makeName: string;
  model: AdminVehicleCatalogModelDto;
  syncing: boolean;
  write: UseCatalogWriteResult;
  onOpenMake: (makeId: string) => void;
  onClose: () => void;
}

const FLAGS = ['popular', 'onSale', 'legacy', 'hidden'] as const;
const MODELFORMSCHEMA_RESOLVER = zodResolver(modelFormSchema);
const FORM_ID = 'vehicle-catalog-model-form';

export const ModelEditDialog = (p: ModelEditDialogProps): ReactElement => {
  const { t } = useTranslation();
  const cache = useCatalogCache();
  const entity = useEntityForm<
    ModelFormValues,
    AdminVehicleCatalogPatchModelBody,
    AdminVehicleCatalogModelDto
  >({
    resolver: MODELFORMSCHEMA_RESOLVER,
    values: modelValues(p.model),
    row: p.model,
    syncing: p.syncing,
    write: p.write,
    bodyOf: (v) => modelPatchBody(v, p.model),
    send: (body) => patchCatalogModel(p.model.id, body),
    apply: (result) => cache.spliceModel(p.makeId, result),
    successKey: 'vehicleCatalog.models.saved',
  });

  return (
    <Dialog open onClose={p.write.busy ? undefined : p.onClose} maxWidth="md" fullWidth>
      <DialogTitle>{t('vehicleCatalog.modelDialog.editTitle', { name: p.model.name })}</DialogTitle>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        {entity.stale ? <StaleBanner /> : null}
        {/* The alias form is a sibling, never nested: Save submits by `form` id. */}
        <form
          id={FORM_ID}
          noValidate
          onSubmit={(e) => {
            void entity.form.handleSubmit(async () => {
              await entity.save();
            })(e);
          }}
        >
          <EntityFields
            entity={entity}
            currentName={p.model.name}
            flags={FLAGS}
            disabled={p.write.busy}
            currentMakeId={p.makeId}
            onOpenMake={p.onOpenMake}
          />
        </form>
        <Box>
          <AliasSection
            title={t('vehicleCatalog.aliases.modelTitle')}
            aliases={p.model.aliases}
            makeId={p.makeId}
            modelId={p.model.id}
            write={p.write}
            disabled={p.write.busy || p.syncing}
            onOpenMake={p.onOpenMake}
          />
        </Box>
        <GenerationsSection
          modelId={p.model.id}
          modelTitle={`${p.makeName} ${p.model.name}`}
          write={p.write}
          disabled={p.syncing}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={p.onClose} disabled={p.write.busy}>
          {t('common.cancel')}
        </Button>
        <Typography variant="caption" color="text.secondary" sx={{ mr: 'auto', ml: 1 }}>
          {t('vehicleCatalog.modelDialog.footNote')}
        </Typography>
        <Button
          type="submit"
          form={FORM_ID}
          variant="contained"
          disabled={!entity.canSave}
          loading={p.write.busy}
        >
          {t('common.save')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
